import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import AuthService from '../Api_Module/Api_Services/AuthService';
import { UserContext } from '../context/UserContext';

interface ProtectedRouteProps {
  openPopup: () => void;
  closePopup: () => void;
  requireSubscription?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  openPopup, 
  closePopup,
  requireSubscription = false 
}) => {
  const userContext = useContext(UserContext);
  const { userData } = userContext || {};
  const location = useLocation();
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = sessionStorage.getItem('STOCK_AUTH_TOKEN');
        
        if (!token) {
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate('/', { replace: true });
          openPopup();
          return;
        }

        // Validate token
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          const isTokenValid = decoded.exp > Date.now() / 1000;
          
          if (!isTokenValid) {
            // Token expired
            sessionStorage.removeItem('STOCK_AUTH_TOKEN');
            sessionStorage.removeItem('userId');
            sessionStorage.removeItem('userProfile');
            setIsAuthenticated(false);
            setIsLoading(false);
            navigate('/', { replace: true });
            openPopup();
            return;
          }

          // Token is valid
          setIsAuthenticated(true);
          closePopup(); // Close popup if user is authenticated

          // Check subscription if required
          if (requireSubscription) {
            try {
              const userData = await AuthService.getUserData();
              console.log('User Data:', userData);
              const hasActiveSubscription = !!userData?.stripeSubscriptionId && 
                (userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing');
              setHasSubscription(hasActiveSubscription);
            } catch (error) {
              console.error('Error fetching user data:', error);
              setHasSubscription(false);
            }
          }

        } catch (e) {
          // Invalid token format
          sessionStorage.removeItem('STOCK_AUTH_TOKEN');
          sessionStorage.removeItem('userId');
          sessionStorage.removeItem('userProfile');
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate('/', { replace: true });
          openPopup();
          return;
        }
      } catch (error) {
        console.error('Error in auth check:', error);
        setIsAuthenticated(false);
        navigate('/', { replace: true });
        openPopup();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [requireSubscription, openPopup, closePopup, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-theme-primary">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full mb-4 mx-auto"></div>
          <p className="text-theme-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Return null while popup handles auth
  }

  // Authenticated but subscription required and not found - show subscription message
  if (requireSubscription && hasSubscription === false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-theme-primary">
          <h2 className="text-xl font-semibold mb-2">Subscription Required</h2>
          <p>Please upgrade your subscription to access this feature.</p>
        </div>
      </div>
    );
  }

  // Check if user has valid subscription or active trial
  const hasValidAccess = userData && (
    (userData.subscriptionStatus === 'active' && userData.planActive === true) ||
    (userData.subscriptionStatus === 'trialing' && userData.planActive === true && userData.isTrial === true)
  );
  console.log("Has Valid Access:", hasValidAccess);

  let path = location.pathname.toLowerCase();
  if (!hasValidAccess && hasValidAccess != null && path !== '/profile') {
    return <Navigate to="/pricing" replace />;
  }

  // All checks passed - render the protected content
  return <Outlet />;
};

export default ProtectedRoute;